import { useEffect, useRef } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';
import { useExpoPushNotifications } from '../hooks/useExpoPushNotifications';
import { useAuth } from '../hooks/useAuth';

// Debug logging helper for push notifications manager
const logPushManager = (context: string, data: Record<string, unknown>) => {
  const prefix = `🔔 [PushNotificationsManager][${Platform.OS.toUpperCase()}]`;
  console.log(`${prefix} ${context}:`, JSON.stringify(data, null, 2));
};

export function PushNotificationsManager() {
  const { user } = useAuth();
  const { registerAsync, unregisterAsync, error, pushToken, permissionStatus, isDevice } = useExpoPushNotifications();
  const lastUserIdRef = useRef<string | null>(null);
  const hasRegisteredForCurrentUser = useRef(false);

  // Log initial mount
  useEffect(() => {
    logPushManager('Component mounted', {
      userId: user?.id,
      isDevice,
      permissionStatus,
      pushToken,
      platform: Platform.OS,
    });
  }, []);

  useEffect(() => {
    const userId = user?.id ?? null;

    logPushManager('User state changed', {
      userId,
      lastUserId: lastUserIdRef.current,
      hasRegisteredForCurrentUser: hasRegisteredForCurrentUser.current,
      isDevice,
    });

    if (userId) {
      if (!hasRegisteredForCurrentUser.current || lastUserIdRef.current !== userId) {
        logPushManager('Initiating registration', { userId, reason: 'user login or change' });
        registerAsync(userId)
          .then((result) => {
            logPushManager('Registration completed', {
              userId,
              token: result.token,
              status: result.status,
            });
          })
          .catch((err) => {
            logPushManager('Registration FAILED', {
              userId,
              error: err?.message,
              stack: err?.stack,
            });
            console.warn('Failed to register for push notifications', err);
          });
        hasRegisteredForCurrentUser.current = true;
      }
    } else if (lastUserIdRef.current) {
      logPushManager('Initiating unregistration', { userId: lastUserIdRef.current, reason: 'user logout' });
      unregisterAsync(lastUserIdRef.current).catch((err) => {
        logPushManager('Unregistration FAILED', {
          userId: lastUserIdRef.current,
          error: err?.message,
        });
        console.warn('Failed to unregister push notifications on sign-out', err);
      });
      hasRegisteredForCurrentUser.current = false;
    }

    lastUserIdRef.current = userId;
  }, [user?.id, registerAsync, unregisterAsync, isDevice]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    // Skip AppState listener on web (not relevant)
    if (Platform.OS === 'web') {
      return;
    }

    const handleAppStateChange = (nextState: AppStateStatus) => {
      logPushManager('App state changed', { nextState, userId: user?.id });
      if (nextState === 'active' && user?.id) {
        logPushManager('Refreshing registration on foreground', { userId: user.id });
        registerAsync(user.id)
          .then((result) => {
            logPushManager('Foreground registration refresh completed', {
              userId: user.id,
              token: result.token,
              status: result.status,
            });
          })
          .catch((err) => {
            logPushManager('Foreground registration refresh FAILED', {
              userId: user.id,
              error: err?.message,
            });
            console.warn('Failed to refresh push notification registration', err);
          });
      }
    };

    const subscription = AppState.addEventListener('change', handleAppStateChange);
    return () => subscription.remove();
  }, [user?.id, registerAsync]);

  useEffect(() => {
    if (error) {
      logPushManager('Error state updated', { error });
      console.warn('[PushNotificationsManager] Push notifications error:', error);
    }
  }, [error]);

  // Log token updates
  useEffect(() => {
    if (pushToken) {
      logPushManager('Push token available', {
        token: pushToken,
        userId: user?.id,
        permissionStatus,
      });
    }
  }, [pushToken, user?.id, permissionStatus]);

  return null;
}

export default PushNotificationsManager;
